import { animated, useSpring, config } from "@react-spring/web";
import circleCheckFilledIcon from "@tabler/icons/filled/circle-check.svg";
import checkIcon from "@tabler/icons/outline/check.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { cn } from "@/utils/cn";

import { HStack, Icon, Text } from "../ui";

import type {
  Poll as PollEntity,
  PollOption as PollOptionEntity,
} from "@/schemas";

const messages = defineMessages({
  voted: { id: "poll.voted", defaultMessage: "You voted for this answer" },
  votes: {
    id: "poll.votes",
    defaultMessage: "{votes, plural, one {# vote} other {# votes}}",
  },
});

const PollPercentageBar = ({ percent }: { percent: number }) => {
  const barSpring = useSpring({
    width: `${percent}%`,
    from: { width: "0%" },
    config: config.gentle,
  });
  return (
    <animated.span
      className="absolute inset-0 inline-block h-full rounded-l-md bg-primary-200 dark:bg-primary-500"
      style={barSpring}
    />
  );
};

interface PollOptionTextProps extends PollOptionProps {
  percent: number;
}

const PollOptionText: React.FC<PollOptionTextProps> = ({
  poll,
  option,
  index,
  active,
  onToggle,
}) => {
  const handleOptionChange: React.EventHandler<React.ChangeEvent> = () =>
    onToggle(index);

  const handleOptionKeyPress: React.EventHandler<React.KeyboardEvent> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      onToggle(index);
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <label
      className={cn(
        "relative flex cursor-pointer rounded-3xl border border-solid p-2 hover:bg-primary-100 dark:hover:bg-primary-800/50",
        {
          "border-primary-600 ring-1 ring-primary-600 dark:border-primary-300 dark:ring-primary-300":
            active,
          "border-primary-500 dark:border-primary-400": !active,
        },
      )}
    >
      <input
        className="hidden"
        name="vote-options"
        type={poll.multiple ? "checkbox" : "radio"}
        value={index}
        checked={active}
        onChange={handleOptionChange}
      />

      <div className="grid w-full items-center">
        <div className="col-start-1 row-start-1 mr-6 ml-4 justify-self-center">
          <div className="text-primary-600 dark:text-white">
            <Text
              theme="inherit"
              weight="medium"
              dangerouslySetInnerHTML={{ __html: option.title_emojified }}
            />
          </div>
        </div>

        {poll.multiple && (
          <div className="col-start-1 row-start-1 flex items-center justify-self-end">
            <span
              className={cn(
                "flex size-6 flex-none items-center justify-center rounded-full border border-solid",
                {
                  "border-primary-600 bg-primary-600 dark:border-primary-300 dark:bg-primary-300":
                    active,
                  "border-transparent bg-transparent": !active,
                },
              )}
              tabIndex={0}
              role="checkbox"
              onKeyPress={handleOptionKeyPress}
              aria-checked={active}
              aria-label={option.title}
            >
              {active && (
                <Icon
                  src={checkIcon}
                  className="size-4 text-white dark:text-primary-900"
                />
              )}
            </span>
          </div>
        )}
      </div>
    </label>
  );
};

interface PollOptionProps {
  poll: PollEntity;
  option: PollOptionEntity;
  index: number;
  showResults?: boolean;
  active: boolean;
  onToggle: (value: number) => void;
}

const PollOption = (props: PollOptionProps) => {
  const { index, poll, option, showResults } = props;

  const intl = useIntl();

  if (!poll) return null;

  const pollVotesCount = poll.votes_count || poll.voters_count;
  const percent =
    pollVotesCount === 0 ? 0 : (option.votes_count / pollVotesCount) * 100;
  const voted = poll.own_votes?.includes(index);
  const message = intl.formatMessage(messages.votes, {
    votes: option.votes_count,
  });

  return (
    <div key={option.title}>
      {showResults ? (
        <div title={voted ? message : undefined}>
          <HStack
            justifyContent="between"
            alignItems="center"
            className="relative w-full overflow-hidden rounded-md bg-gray-100 p-2 dark:bg-primary-900"
          >
            <PollPercentageBar percent={percent} />

            <div className="text-primary-600 dark:text-white">
              <Text
                theme="inherit"
                weight="medium"
                dangerouslySetInnerHTML={{ __html: option.title_emojified }}
                className="relative"
              />
            </div>

            <HStack space={2} alignItems="center" className="relative">
              {voted ? (
                <Icon
                  src={circleCheckFilledIcon}
                  alt={intl.formatMessage(messages.voted)}
                  className="size-4 text-primary-500 dark:text-white"
                />
              ) : (
                <div className="svg-icon" />
              )}

              <div className="text-primary-600 dark:text-white">
                <Text weight="medium" theme="inherit">
                  {Math.round(percent)}%
                </Text>
              </div>
            </HStack>
          </HStack>
        </div>
      ) : (
        <PollOptionText percent={percent} {...props} />
      )}
    </div>
  );
};

export default PollOption;
